import { TenantResolutionError } from './errors';

const DNS_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

const normalizePort = (port: string): void => {
  if (!/^\d+$/.test(port)) {
    throw new TenantResolutionError('MALFORMED_HOST', 'Hostname contains an invalid port.');
  }
  const numericPort = Number(port);
  if (numericPort < 1 || numericPort > 65535) {
    throw new TenantResolutionError('MALFORMED_HOST', 'Hostname port is out of range.');
  }
};

const isValidIpv4 = (hostname: string): boolean => {
  const parts = hostname.split('.');
  return parts.length === 4 && parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const value = Number(part);
    return value >= 0 && value <= 255 && String(value) === part;
  });
};

export const normalizeHostname = (input: string): string => {
  if (!input || input !== input.trim() || /\s/.test(input) || input.includes('://')) {
    throw new TenantResolutionError('MALFORMED_HOST', 'Hostname is missing or malformed.');
  }

  let hostname = input.toLowerCase();

  if (hostname.startsWith('[')) {
    const closingBracket = hostname.indexOf(']');
    if (closingBracket === -1) {
      throw new TenantResolutionError('MALFORMED_HOST', 'IPv6 hostname is malformed.');
    }
    const address = hostname.slice(1, closingBracket);
    const remainder = hostname.slice(closingBracket + 1);
    if (!address || !/^[0-9a-f:]+$/.test(address)) {
      throw new TenantResolutionError('MALFORMED_HOST', 'IPv6 hostname is malformed.');
    }
    if (remainder) {
      if (!remainder.startsWith(':')) {
        throw new TenantResolutionError('MALFORMED_HOST', 'Hostname is malformed.');
      }
      normalizePort(remainder.slice(1));
    }
    return address;
  }

  const colonCount = (hostname.match(/:/g) ?? []).length;
  if (colonCount > 1) {
    throw new TenantResolutionError('MALFORMED_HOST', 'IPv6 hostnames must use brackets.');
  }
  if (colonCount === 1) {
    const separator = hostname.lastIndexOf(':');
    normalizePort(hostname.slice(separator + 1));
    hostname = hostname.slice(0, separator);
  }

  if (hostname.endsWith('.')) {
    hostname = hostname.slice(0, -1);
  }
  if (!hostname || hostname.length > 253 || hostname.endsWith('.')) {
    throw new TenantResolutionError('MALFORMED_HOST', 'Hostname is malformed.');
  }

  if (hostname === 'localhost' || isValidIpv4(hostname)) {
    return hostname;
  }

  const labels = hostname.split('.');
  if (!labels.every((label) => DNS_LABEL_PATTERN.test(label))) {
    throw new TenantResolutionError('MALFORMED_HOST', 'Hostname contains an invalid DNS label.');
  }

  return hostname;
};

export const isLocalHostname = (hostname: string): boolean => {
  const normalized = normalizeHostname(hostname);
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1';
};

export const getScheduifySubdomainSlug = (hostname: string): string | undefined => {
  const normalized = normalizeHostname(hostname);
  const suffix = '.scheduify.com';
  if (!normalized.endsWith(suffix)) {
    return undefined;
  }
  const slug = normalized.slice(0, -suffix.length);
  return slug && !slug.includes('.') ? slug : undefined;
};
